import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Activity, Building2, Users, Stethoscope, HeartPulse, AlertOctagon,
  DollarSign, CalendarDays, Sparkles, FileSpreadsheet, LogOut, LayoutDashboard, UploadCloud, MapPin, ListChecks, Settings as SettingsIcon,
} from "lucide-react";import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";

const navGroups = [
  {
    label: "Command",
    items: [
      { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
      { to: "/todos", label: "Todo list", icon: ListChecks },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/facilities", label: "Facilities", icon: Building2 },
      { to: "/nps", label: "Nurse Practitioners", icon: Users },
      { to: "/directors", label: "Medical Directors", icon: Stethoscope },
      { to: "/cnas", label: "CNAs", icon: HeartPulse },
    ],
  },
  {
    label: "Workflow",
    items: [
      { to: "/issues", label: "Issues", icon: AlertOctagon },
      { to: "/claims", label: "Claims", icon: DollarSign },
      { to: "/meetings", label: "Calendar", icon: CalendarDays },
    ],
  },
  {
    label: "Clinical",
    items: [
      { to: "/outcomes/a1c", label: "A1C Report", icon: Activity },
    ],
  },
  {
    label: "Tools",
    items: [
      { to: "/ai", label: "Ask AI", icon: Sparkles },
      { to: "/coverage-planner", label: "Coverage Planner", icon: MapPin },
      { to: "/coverage-map", label: "Coverage Map", icon: MapPin },
      { to: "/smart-import", label: "Smart Import", icon: UploadCloud },
      { to: "/enrich-addresses", label: "Address Enrichment", icon: MapPin },
      { to: "/cpt-prices", label: "CPT Prices", icon: DollarSign },
      { to: "/settings", label: "Settings", icon: SettingsIcon },
      { to: "/import-export", label: "Import / Export", icon: FileSpreadsheet },
    ],
  },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen w-full flex bg-[#F7F5F0]">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 lg:w-72 flex-col border-r border-[#E5E0D8] bg-white sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-[#E5E0D8] flex items-center gap-2">
          <div className="h-9 w-9 rounded-md bg-[#4A6B5D] grid place-items-center text-white">
            <Activity className="h-5 w-5" />
          </div>
          <div>
            <div className="heading font-semibold tracking-tight">Dialog</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#8A847A]">Command Center</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto" data-testid="sidebar-nav">
          {navGroups.map((g) => (
            <div key={g.label}>
              <div className="px-3 pb-1.5 text-[10px] uppercase tracking-[0.15em] text-[#8A847A] font-semibold">
                {g.label}
              </div>
              <div className="space-y-0.5">
                {g.items.map(({ to, label, icon: Icon, end }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                        isActive
                          ? "bg-[#F0EBE1] text-[#292723] font-medium"
                          : "text-[#5C5850] hover:bg-[#F0EBE1]"
                      }`
                    }
                  >
                    <Icon className="h-4 w-4" />
                    <span>{label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-[#E5E0D8]">
          <div className="text-xs text-[#8A847A]">Signed in as</div>
          <div className="text-sm font-medium truncate" data-testid="current-user-email">{user?.email}</div>
          <Button
            variant="ghost" size="sm"
            className="mt-3 w-full justify-start text-[#5C5850] hover:bg-[#F0EBE1]"
            onClick={async () => { await logout(); nav("/login"); }}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-[#E5E0D8] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-[#4A6B5D] grid place-items-center text-white">
            <Activity className="h-4 w-4" />
          </div>
          <span className="heading font-semibold">Dialog</span>
        </div>
        <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/login"); }} data-testid="mobile-logout-btn">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>

      <main className="flex-1 min-w-0 pt-14 md:pt-0 pb-24 md:pb-0">
        {/* Mobile bottom nav — top 4 items */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E5E0D8] flex">
          {[
            { to: "/", label: "Home", icon: LayoutDashboard, end: true },
            { to: "/facilities", label: "Facilities", icon: Building2 },
            { to: "/nps", label: "NPs", icon: Users },
            { to: "/smart-import", label: "Import", icon: UploadCloud },
            { to: "/ai", label: "AI", icon: Sparkles },
          ].map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center gap-0.5 py-2 text-[10px] ${
                  isActive ? "text-[#4A6B5D] font-medium" : "text-[#5C5850]"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </div>

        <Outlet />
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}
